<template>
  <div>
    <!-- Example: Basic Modal Usage -->
    <button @click="$modal.show('basic-modal')">Open Basic Modal</button>
    
    <themed-modal
      name="basic-modal"
      title="Basic Modal"
      size="md"
    >
      <p>This is a basic themed modal with default settings.</p>
    </themed-modal>

    <!-- Example: Modal with Footer -->
    <button @click="$modal.show('modal-with-footer')">Open Modal with Footer</button>
    
    <themed-modal
      name="modal-with-footer"
      title="Confirm Action"
      size="sm"
      :show-footer="true"
    >
      <p>Are you sure you want to proceed with this action?</p>
      
      <template #footer>
        <button class="themed-button themed-button-secondary" @click="$modal.hide('modal-with-footer')">
          Cancel
        </button>
        <button class="themed-button themed-button-primary" @click="handleConfirm">
          Confirm
        </button>
      </template>
    </themed-modal>

    <!-- Example: Large Modal with Custom Content -->
    <button @click="$modal.show('large-modal')">Open Large Modal</button>
    
    <themed-modal
      name="large-modal"
      size="lg"
      :click-to-close="false"
    >
      <template #header>
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Custom Header</span>
        </div>
      </template>

      <div class="space-y-4">
        <p>This modal has a custom header and cannot be closed by clicking outside.</p>
        <p>It demonstrates the scrollable content feature when content is long.</p>
        <div v-for="i in 10" :key="i" class="p-4 bg-muted rounded">
          Content block {{ i }}
        </div>
      </div>

      <template #footer>
        <button class="themed-button themed-button-primary" @click="$modal.hide('large-modal')">
          Close
        </button>
      </template>
    </themed-modal>

    <!-- Example: Full Screen Modal -->
    <button @click="$modal.show('fullscreen-modal')">Open Fullscreen Modal</button>
    
    <themed-modal
      name="fullscreen-modal"
      title="Full Screen View"
      size="full"
    >
      <div class="h-full flex items-center justify-center">
        <p class="text-2xl">This modal takes up most of the screen</p>
      </div>
    </themed-modal>
  </div>
</template>

<script>
export default {
  name: 'ThemedModalExample',
  methods: {
    handleConfirm() {
      this.$modal.hide('modal-with-footer');
      this.$toast.success('Action confirmed!');
    }
  }
}
</script>

<!-- 
USAGE DOCUMENTATION:

Props:
- name (required): Unique identifier for the modal
- title: Modal title (shown in header)
- size: 'sm' | 'md' | 'lg' | 'xl' | 'full' (default: 'md')
- width: Custom width (overrides size)
- height: Custom height (default: 'auto')
- adaptive: Responsive sizing (default: true)
- scrollable: Enable body scrolling (default: true)
- clickToClose: Close on backdrop click (default: true)
- showHeader: Show header section (default: true)
- showFooter: Show footer section (default: false)
- showClose: Show close button (default: true)

Slots:
- default: Modal body content
- header: Custom header content
- footer: Footer content (buttons, actions)

Events:
- before-open: Fired before modal opens
- opened: Fired after modal opens
- before-close: Fired before modal closes
- closed: Fired after modal closes

Methods (via $modal):
- $modal.show('modal-name'): Open modal
- $modal.hide('modal-name'): Close modal
- $modal.toggle('modal-name'): Toggle modal

Features:
- Theme-aware styling (light/dark mode)
- Focus trap (Tab navigation stays within modal)
- Keyboard navigation (Escape to close, Tab to navigate)
- Accessible (ARIA labels, focus management)
- Smooth animations
- Backdrop blur effect
- Custom scrollbar styling
-->
